import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const InputContainer = styled(Box)(({ theme, error }) => ({
  borderRadius: 4,
  position: "relative",
  backgroundColor: theme.palette.mode === "light" ? "#fff" : "#2b2b2b",
  border: `1px solid ${
    error
      ? theme.palette.error.main
      : theme.palette.mode === "dark"
      ? "#555"
      : "#ccc"
  }`,
  height: 34,
  maxHeight: 34,
  boxSizing: "border-box",
  padding: "0 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  boxShadow: "inset 0 1px 1px rgb(0 0 0 / 8%)",
  transition: theme.transitions.create([
    "border-color",
    "background-color",
    "box-shadow",
  ]),
  userSelect: "none",

  "&:hover": {
    borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "light" ? "#fafafa" : "#333",
  },

  "&:focus-within, &:focus": {
    boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
    borderColor: theme.palette.primary.main,
    outline: "none",
  },
}));

const BrowseBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: "12px",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: 3,
  backgroundColor:
    theme.palette.mode === "light" ? "#f0f4f8" : "#3a3a3a",
  color: theme.palette.primary.main,
  border: `1px solid ${
    theme.palette.mode === "light" ? "#d0dbe7" : "#4a4a4a"
  }`,
  flexShrink: 0,
}));

export default function CustomFileInput({
  id,
  name,
  onChange,
  accept,
  error,
  value,
  placeholder = "Choose file...",
  buttonText = "Browse",
  disabled = false,
  sx = {},
}) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
    }
    if (onChange) {
      onChange(e);
    }
  };

  // Extract filename if value is a URL string
  const getDisplayName = () => {
    if (selectedFileName) return selectedFileName;
    if (typeof value === "string" && value.trim()) {
      const parts = value.split("/");
      return parts[parts.length - 1] || value;
    }
    return "";
  };

  const displayName = getDisplayName();

  return (
    <InputContainer
      error={error}
      onClick={handleClick}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
        width: "100%",
        ...sx,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        id={id}
        name={name}
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: 1,
          mr: 1,
        }}
      >
        {displayName && (
          <InsertDriveFileOutlinedIcon
            fontSize="small"
            sx={{
              fontSize: "16px",
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: displayName
              ? theme.palette.text.primary
              : theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayName || placeholder}
        </Typography>
      </Box>

      <BrowseBadge>
        <CloudUploadOutlinedIcon sx={{ fontSize: "14px" }} />
        <span>{buttonText}</span>
      </BrowseBadge>
    </InputContainer>
  );
}
